import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { billing } from '@platform/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  KeyValueList,
  KeyValueSkeleton,
  Page,
  PageHeader,
  Stack,
  Typography,
  useToast,
  type BadgeTone,
} from '@platform/design-system';
import { describeError, useSession } from '@platform/app-kit';
import { useBilling, useBillingPortal, usePlans } from '../api/queries.ts';
import { PRODUCT_NAME } from '../shell/brand.ts';
import { useDocumentTitle } from '../shell/document-title.ts';

const STATUS: Record<billing.SubscriptionStatus, { tone: BadgeTone; label: string }> = {
  active: { tone: 'success', label: 'Active' },
  trialing: { tone: 'info', label: 'Trial' },
  incomplete: { tone: 'warning', label: 'Payment pending' },
  past_due: { tone: 'warning', label: 'Payment needed' },
  unpaid: { tone: 'warning', label: 'Payment needed' },
  paused: { tone: 'neutral', label: 'Paused' },
  cancelled: { tone: 'neutral', label: 'Cancelled' },
  none: { tone: 'neutral', label: 'Not started' },
};

/** A date in the person's own time zone (DS-CONTENT-04). */
function day(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'long' });
}

/**
 * `/` — the signed-in home of a personal account: the plan and its status, the account, and a
 * plain note that the app itself is on its way. Nothing pretends to be data that isn't there.
 */
export function HomePage() {
  const { principal, signOut } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const plan = useBilling();
  const plans = usePlans();
  const portal = useBillingPortal();
  useDocumentTitle('Home');

  const firstName =
    principal?.name && principal.name !== principal.email
      ? principal.name.split(' ')[0]
      : undefined;
  const summary = plan.data;
  const planName =
    plans.data?.find((p) => p.packageId === summary?.packageId)?.name ?? 'Monthly plan';

  const openPortal = () =>
    portal.mutate(undefined, {
      onSuccess: (url) => {
        const target = new URL(url, window.location.origin);
        if (target.origin === window.location.origin)
          void navigate(`${target.pathname}${target.search}`);
        else window.location.assign(target.toString());
      },
      onError: (e) => toast.error(describeError(e, 'We couldn’t open billing. Try again.')),
    });

  let planBody: ReactNode;
  if (plan.isLoading) planBody = <KeyValueSkeleton rows={3} />;
  else if (plan.isError || !summary)
    planBody = (
      <Alert
        severity="warning"
        action={
          <Button size="small" onClick={() => void plan.refetch()}>
            Try again
          </Button>
        }
      >
        {describeError(plan.error, 'We couldn’t load your plan just now.')}
      </Alert>
    );
  else {
    const status = STATUS[summary.status];
    const items: { key: string; value: ReactNode }[] = [
      { key: 'Plan', value: planName },
      { key: 'Status', value: <Badge tone={status.tone} label={status.label} /> },
    ];
    if (summary.currentPeriodEnd)
      items.push({
        key: summary.cancelAtPeriodEnd ? 'Ends on' : 'Renews on',
        value: day(summary.currentPeriodEnd),
      });
    planBody = (
      <Stack spacing={2}>
        {(summary.status === 'past_due' || summary.status === 'unpaid') && (
          <Alert severity="warning">
            Your last payment didn’t go through. Update your card in billing to keep your plan.
          </Alert>
        )}
        <KeyValueList items={items} />
      </Stack>
    );
  }

  return (
    <Page width="narrow">
      <PageHeader
        title={firstName ? `Welcome, ${firstName}` : 'Welcome'}
        meta={`Your ${PRODUCT_NAME} account`}
      />

      <Card
        title="Your plan"
        action={
          summary?.portalAvailable ? (
            <Button
              onClick={openPortal}
              busy={portal.isPending}
              busyLabel="Opening billing…"
              size="small"
            >
              Manage billing
            </Button>
          ) : undefined
        }
      >
        {planBody}
      </Card>

      <Card title="Coming soon">
        <Typography variant="body2" color="text.secondary">
          We’re building the place for your readings and the trends behind them. It will appear
          right here, and we’ll email you when it’s ready.
        </Typography>
      </Card>

      <Card
        title="Account"
        action={
          <Button
            size="small"
            onClick={() => void signOut().then(() => navigate('/login', { replace: true }))}
          >
            Sign out
          </Button>
        }
      >
        <KeyValueList
          items={[
            { key: 'Name', value: principal?.name ?? '' },
            { key: 'Email', value: principal?.email ?? '' },
          ]}
        />
      </Card>
    </Page>
  );
}
